import { createContext, useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { auth } from '../../firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, createUserWithEmailAndPassword, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { getLiquidListener, getOrganization, getWidgetsListener } from './activeListeners';
import { useHandleFiles } from '../hooks/useHandleFiles';
import { useQrStates } from './useQrStates';
import adminAPI from '../../API/adminAPI';

// Cookie utility functions
const setCookie = (name, value, days = 30) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    // Set domain to .liquidqr.com to make cookies accessible from all subdomains
    // Adding SameSite=None to allow cross-subdomain use and Secure to enforce HTTPS
    document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=.liquidqr.com; SameSite=None; Secure`;
};

// Save this for later use
// const getCookie = (name) => {
//     const value = `; ${document.cookie}`;
//     const parts = value.split(`; ${name}=`);
//     if (parts.length === 2) return parts.pop().split(';').shift();
//     return null;
// };

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
};

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userObj, setUserObj] = useState(null);
    // Tracks the currently active organization (persisted manually via localStorage)
    const [activeOrgId, setActiveOrgIdState] = useState(null);
    const [liquidQRs, setLiquidQRs] = useState([]);
    const [deletedQrs, setDeletedQrs] = useState([]);
    const [orgObj, setOrgObj] = useState(null);
    const [config, setConfig] = useState(null);
    const [widgets, setWidgets] = useState(null);
    const [currentUserOrgMemberData, setCurrentUserOrgMemberData] = useState(null);
	// for gen sign up
	const [isRegistering, setIsRegistering] = useState(false);

    // Granular loading state tracking
    const [loadingState, setLoadingState] = useState({
        auth: true,
        user: 'idle',      // 'idle' | 'loading' | 'success' | 'error'
        org: 'idle',
        liquidQRs: 'idle',
        widgets: 'idle',
        config: 'idle'
    });

    const { qr_options, optionsDispatch, qr_data, dataDispatch } = useQrStates();
    const fileHandler = useHandleFiles();
    const prevActiveOrgIdRef = useRef(null);

    // Initialize activeOrgId from localStorage on mount
    useEffect(() => {
        try {
            const stored = window.localStorage.getItem('activeOrgId');
            if (stored !== null && stored !== 'undefined') {
                // Parse JSON and set state directly (bypass wrapper to avoid isLoading check)
                setActiveOrgIdState(JSON.parse(stored));
            }
        } catch (err) {
            console.error('Failed to read activeOrgId from localStorage:', err);
        }
    }, []);

    // Compute loading state - only critical data blocks the app
    const isLoading = useMemo(() => {
        if (!currentUser) return loadingState.auth;

        // Critical data that must load before showing app
        return loadingState.auth || 
               loadingState.user === 'loading' || 
               loadingState.org === 'loading';
    }, [currentUser, loadingState.auth, loadingState.user, loadingState.org]);

    // Wrapper for setActiveOrgId that also persists to localStorage
    const setActiveOrgId = useCallback((orgId) => {
        setActiveOrgIdState(orgId);
        try {
            if (orgId === null || orgId === undefined) {
                window.localStorage.removeItem('activeOrgId');
            } else {
                window.localStorage.setItem('activeOrgId', JSON.stringify(orgId));
            }
        } catch (err) {
            console.error('Failed to persist activeOrgId to localStorage:', err);
        }
    }, [isLoading]);

    // Reset QR state when active organization changes
    useEffect(() => {
        const prevOrgId = prevActiveOrgIdRef.current;

        if (prevOrgId !== null && prevOrgId !== activeOrgId) {
            dataDispatch({ type: 'reset', data: '' });
            optionsDispatch({ type: 'reset', options: { randKey: '' } });
        }

        prevActiveOrgIdRef.current = activeOrgId;
    }, [activeOrgId, dataDispatch, optionsDispatch]);

    // Set up auth state listener once
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user || null);
            setLoadingState(prev => ({ ...prev, auth: false }));

            // If user exists, prepare to load user data
            if (user) {
                setLoadingState(prev => ({ ...prev, user: 'loading' }));
            }
        });
        return () => unsubscribe();
    }, []);

    // separated fetch config docs from listeners to avoid re-fetching on changes
    useEffect(() => {
        const fetchConfig = async () => {
            setLoadingState(prev => ({ ...prev, config: 'loading' }));
            try {
                const { data } = await adminAPI.getConfigDocs();
                if (data) {
                    setConfig(data);
                    setLoadingState(prev => ({ ...prev, config: 'success' }));
                } else {
                    setLoadingState(prev => ({ ...prev, config: 'error' }));
                }
            } catch (err) {
                console.error('Error retrieving config docs:', err);
                setLoadingState(prev => ({ ...prev, config: 'error' }));
            }
        };
        fetchConfig();
    },[]);

    // user data listener (sets up user object and initial active org)
    useEffect(() => {
        let unsubscribeUser;

        if (currentUser?.uid) {
            setLoadingState(prev => ({ ...prev, user: 'loading' }));

            unsubscribeUser = onSnapshot(
                doc(firestore, 'users', currentUser.uid),
                async (snapshot) => {
                    if (snapshot.exists()) {
                        const userData = snapshot.data();
                        setUserObj(userData);
                        setLoadingState(prev => ({ ...prev, user: 'success' }));

                        setCookie('login_timestamp', Date.now().toString());
                        setCookie('user_plan_id', userData?.plan?.plan_id ?? '0');

                        // Determine active org preference:
                        // 1) Use stored activeOrgId if it exists in org_ids
                        // 2) Otherwise use first org_id
                        // 3) Otherwise default to personal org (user uid)
                        const orgIds = userData?.org_ids || [];
                        const storedOrg = activeOrgId;
                        const personalOrg = currentUser.uid;

                        const nextActiveOrgId =
                            (storedOrg && orgIds.includes(storedOrg)) ? storedOrg :
                            orgIds[0] || personalOrg;

                        if (nextActiveOrgId !== activeOrgId) {
                            setActiveOrgId(nextActiveOrgId);
                        }
                    } else {
                        // user doesn't exist
                        setUserObj(null);
                        setLoadingState(prev => ({ ...prev, user: 'error' }));
                    }
                },
                (error) => {
                    console.error('Error listening to user document:', error);
                    setUserObj(null);
                    setLoadingState(prev => ({ ...prev, user: 'error' }));
                }
            );
        } else {
            // No current user so clear states
            setUserObj(null);
            setLoadingState(prev => ({ 
                ...prev, 
                user: 'idle'
            }));
        }

        return () => {
            unsubscribeUser?.();
        };
    }, [currentUser?.uid]);

    // Separate effect for organization listeners based on activeOrgId
    useEffect(() => {
        let unsubscribeOrgListeners = [];

        if (activeOrgId && currentUser?.uid) {
            setLoadingState(prev => ({ 
                ...prev, 
                org: 'loading',
                liquidQRs: 'loading',
                widgets: 'loading'
            }));

            const setupListeners = async () => {
                try {
                    // Set up listeners for the active organization
                    const liquidUnsub = getLiquidListener(
                        activeOrgId, 
                        setLiquidQRs,
                        setDeletedQrs,
                        () => setLoadingState(prev => ({ ...prev, liquidQRs: 'success' }))
                    );
                    const orgUnsub = getOrganization(
                        activeOrgId, 
                        setOrgObj,
                        () => setLoadingState(prev => ({ ...prev, org: 'success' }))
                    );
                    const widgetUnsub = await getWidgetsListener(
                        activeOrgId, 
                        setWidgets,
                        () => setLoadingState(prev => ({ ...prev, widgets: 'success' }))
                    );

                    unsubscribeOrgListeners.push(liquidUnsub, orgUnsub, widgetUnsub);
                } catch (error) {
                    console.error('Error setting up organization listeners:', error);
                    setLoadingState(prev => ({ 
                        ...prev, 
                        org: 'error',
                        liquidQRs: 'error',
                        widgets: 'error'
                    }));
                }
            };

            setupListeners();
        } else {
            // No active org, clear org-related states
            setLiquidQRs([]);
            setDeletedQrs([]);
            setOrgObj(null);
            setWidgets(null);
            setLoadingState(prev => ({ 
                ...prev, 
                org: 'idle',
                liquidQRs: 'idle',
                widgets: 'idle'
            }));
        }

        // Cleanup: unsubscribe from all org listeners when activeOrgId changes or component unmounts
        return () => {
            unsubscribeOrgListeners.forEach(unsub => unsub?.());
        };
    }, [activeOrgId, currentUser?.uid]);

    // Listen to the current user's member data in the active organization
    useEffect(() => {
        let unsubscribeMember;

        if (activeOrgId && currentUser?.uid && !isRegistering) {
            // Listen to the specific member document for the current user in the active org
            unsubscribeMember = onSnapshot(
                doc(firestore, 'organizations', activeOrgId, 'members', currentUser.uid),
                (snapshot) => {
                    if (snapshot.exists()) {
                        setCurrentUserOrgMemberData({
                            uid: snapshot.id,
                            ...snapshot.data()
                        });
                    } else {
                        setCurrentUserOrgMemberData(null);
                    }
                },
                (error) => {
                    console.error('Error listening to member document:', error);
                    setCurrentUserOrgMemberData(null);
                }
            );
        } else {
            setCurrentUserOrgMemberData(null);
        }

        return () => {
            unsubscribeMember?.();
        };
    }, [activeOrgId, currentUser?.uid, isRegistering]);

    // Timeout protection - if data doesn't load within 10 seconds, log error
    useEffect(() => {
        if (!currentUser || !isLoading) return;

        const timeout = setTimeout(() => {
            console.error('Data loading timeout. Loading state:', loadingState);
            // Could add additional error handling here, like showing a retry button
            // or forcing a reload. For now, just logging for debugging.
        }, 10000);

        return () => clearTimeout(timeout);
    }, [currentUser, isLoading, loadingState]);

    // auth callbacks
    const create = useCallback((email, password) => {
        return createUserWithEmailAndPassword(auth, email, password)
            .then((userCredential) => {
                // Set login timestamp cookie when account is created
                setCookie('login_timestamp', Date.now().toString());
                return userCredential;
            });
    }, []);

    // browserLocalPersistence sets the authentication reference to localStorage opposed to indexedDB or cookies, which others claim is causing issues on iOS
    const login = useCallback(async (email, password) => {
        try {
            await setPersistence(auth, browserLocalPersistence);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            // Set login timestamp cookie
            setCookie('login_timestamp', Date.now().toString());
            return userCredential;
        } catch (err) {
            console.error('Error signing in: ', err.message);
            throw(err);
        };
    }, []);

    const logout = useCallback(async () => {
        try {
          // Reset all loading states
          setLoadingState({
              auth: true,
              user: 'idle',
              org: 'idle',
              liquidQRs: 'idle',
              widgets: 'idle',
              config: 'idle'
          });
          // Set logout timestamp cookie before signing out
          setCookie('logout_timestamp', Date.now().toString());
          return await auth.signOut();
        } catch (error) {
          console.error("Error loggin out: ", error.message || "");
        };
    }, []);
    
    const refreshUserToken = useCallback(async () => {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (error) {
          console.error("Error refreshing user token: ", error.message);
          // Re-throw so calling code can handle retry if needed
          throw error;
        }
    }, [auth?.currentUser]);

    const refreshCurrentUser = useCallback(async ({ email, password }) => {
        try {
            await login(email, password);
            await auth.currentUser.getIdToken(true);
        } catch (err) {
            console.error('Error refreshing current user:', err);
            throw new Error('Failed to refresh user. Please log in again.');
        };
    }, [login]);

    // Count QRs per category
    const categoryQrCounts = useMemo(() => {
        const counts = new Map();
        liquidQRs.forEach(qr => {
            const categoryId = qr?.data?.category;
            if (categoryId) {
                counts.set(categoryId, (counts.get(categoryId) || 0) + 1);
            }
        });
        return counts;
    }, [liquidQRs]);

    const value = useMemo(() => ({
        activeOrgId,
        categoryQrCounts,
        create,
        currentUser,
        currentUserOrgMemberData,
        config,
        dataDispatch,
        deletedQrs,
        fileHandler,
        isLoading,
        loadingState,
        liquidQRs,
        login,
        logout,
        orgObj,
        optionsDispatch,
        qr_data,
        qr_options,
        refreshCurrentUser,
        refreshUserToken,
        setActiveOrgId,
        setLiquidQRs,
        widgets,
        userObj,
		isRegistering, 
		setIsRegistering
    }), [
        activeOrgId,
        categoryQrCounts,
        create,
        currentUser,
        currentUserOrgMemberData,
        config,
        dataDispatch,
        deletedQrs,
        fileHandler,
        isLoading,
        loadingState,
        liquidQRs,
        login,
        logout,
        orgObj,
        optionsDispatch,
        qr_data,
        qr_options,
        refreshCurrentUser,
        refreshUserToken,
        setLiquidQRs,
        widgets,
        userObj,
		isRegistering,
		setIsRegistering
    ]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
